type QueryParams = Record<string, string | undefined>;

type FilterPayload = {
  filter: Record<string, string | string[] | null>;
  pagination: {
    limit: number;
    offset: number;
    page: number;
  };
  sort: Record<string, string>;
};

/**
 * Converts query-style parameters (e.g., filters[0][field], pageSize, sort[...])
 * into a structured object containing `filter`, `pagination`, and `sort` sections.
 *
 * @param {QueryParams} queryParams
 * The flat query parameter object typically parsed from a URL or form submission.
 * @returns {FilterPayload}
 * A structured payload object with `filter`, `pagination`, and `sort` keys.
 *
 * @example
 * const query = {
 *   "pageSize": "30",
 *   "current": "1",
 *   "filters[0][field]": "awb_number",
 *   "filters[0][value]": "BBD8TL8UGHZWH0I",
 *   "filters[1][field]": "order_date_time_utc",
 *   "filters[1][value][0]": "2025-07-01T00:00:00Z",
 *   "filters[1][value][1]": "2025-07-07T23:59:59Z",
 *   "sort[created_time_utc]": "desc"
 * };
 *
 * const result = buildFilterPayload(query);
 * // result.filter.awb_number === "BBD8TL8UGHZWH0I"
 * // result.pagination.limit === 30
 * // result.sort.created_time_utc === "desc"
 */
export default function buildFilterPayload(queryParams: QueryParams): FilterPayload {
  const filters: Record<string, string | string[] | null> = {};
  const sort: Record<string, string> = {};

  Object.entries(queryParams).forEach(([key, value]) => {
    const fieldMatch = key.match(/filters\[(\d+)]\[field]/);
    if (fieldMatch) {
      const index = fieldMatch[1];
      const field = queryParams[`filters[${index}][field]`];
      const value0 = queryParams[`filters[${index}][value][0]`];
      const value1 = queryParams[`filters[${index}][value][1]`];
      const directValue = queryParams[`filters[${index}][value]`];

      if (field) {
        if (value0 !== undefined && value1 !== undefined) {
          filters[field] = [value0, value1];
        } else {
          filters[field] = directValue ?? null;
        }
      }
    }

    const sortMatch = key.match(/^sort\[(.+)]$/);
    if (sortMatch && value) {
      const field = sortMatch[1];
      sort[field] = value;
    }
  });

  const pagination = {
    limit: parseInt(queryParams.pageSize || '0', 10),
    page: parseInt(queryParams.current || '0', 10),
    offset: 0,
  };

  return {
    filter: filters,
    pagination,
    sort,
  };
}
